import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Message } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { ServicePortAddCard, ServicePortCard } from './ServicePortCard';
import { ServicePortModal } from './ServicePortModal';
import { APP_INSTANCE_USAGE_SCENARIO_OPTIONS } from './constants';
import styles from './index.module.less';
import type { ServiceFormItem, ServicePortListEditorProps } from './types';
import {
  applyUsageScenarioToServiceItem,
  getDuplicateServiceNameError,
  getDuplicateServicePortError,
  getUniqueUsageScenarioError,
  readServiceListFromFormValue,
} from './utils';

/** 服务端口列表编辑器（卡片 + 弹窗；ServiceBlock 共用） */
export function ServicePortListEditor({
  serviceList,
  usageScenarioOptions = APP_INSTANCE_USAGE_SCENARIO_OPTIONS,
  requireAtLeastOne = true,
  maxCount,
  canAdd = true,
  canEdit = true,
  canRemove = true,
  isItemLocked,
  onAdd,
  onUpdate,
  onRemove,
}: ServicePortListEditorProps) {
  const { t } = useTranslation();
  const [modalForm] = Form.useForm();
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEditingIndex(null);
    modalForm.resetFields();
  }, [modalForm]);

  const openAdd = useCallback(() => {
    setModalMode('add');
    setEditingIndex(null);
    modalForm.resetFields();
    modalForm.setFieldsValue({ _isDefault: false });
  }, [modalForm]);

  const openEdit = useCallback(
    (index: number) => {
      const current = serviceList[index];
      if (!current) return;
      setModalMode('edit');
      setEditingIndex(index);
      modalForm.setFieldsValue({ ...current });
    },
    [modalForm, serviceList],
  );

  const handleModalOk = useCallback(async () => {
    const isBuiltin = modalMode === 'edit' && editingIndex !== null && !!serviceList[editingIndex]?._isDefault;
    const validateFields = isBuiltin ? ['internalPort'] : ['name', 'usageScenario', 'internalPort'];

    try {
      await modalForm.validate(validateFields);
    } catch {
      return Promise.reject();
    }

    const rawValues = modalForm.getFieldsValue() as ServiceFormItem;
    const values = applyUsageScenarioToServiceItem(rawValues);
    const nextList =
      modalMode === 'add'
        ? [...serviceList, values]
        : modalMode === 'edit' && editingIndex !== null
          ? serviceList.map((item, index) => (index === editingIndex ? { ...item, ...values } : item))
          : serviceList;

    const uniqueScenarioError = getUniqueUsageScenarioError(nextList);
    if (uniqueScenarioError) {
      Message.error(uniqueScenarioError);
      return Promise.reject();
    }

    const duplicateNameError = getDuplicateServiceNameError(nextList);
    if (duplicateNameError) {
      Message.error(duplicateNameError);
      return Promise.reject();
    }

    const duplicatePortError = getDuplicateServicePortError(nextList);
    if (duplicatePortError) {
      Message.error(duplicatePortError);
      return Promise.reject();
    }

    if (typeof maxCount === 'number' && nextList.length > maxCount) {
      Message.error(
        t('data-center:maas.inference.service.max', {
          max: maxCount,
          label: t('data-center:maas.inference.service.title'),
        }),
      );
      return Promise.reject();
    }

    if (modalMode === 'add') {
      onAdd(values);
    } else if (modalMode === 'edit' && editingIndex !== null) {
      onUpdate(editingIndex, { ...serviceList[editingIndex], ...values });
    }

    closeModal();
  }, [closeModal, editingIndex, maxCount, modalForm, modalMode, onAdd, onUpdate, serviceList, t]);

  const modalVisible = modalMode !== null;
  const modalIsDefault = modalMode === 'edit' && editingIndex !== null && !!serviceList[editingIndex]?._isDefault;

  return (
    <>
      <div className={styles.list}>
        {serviceList.map((item, index) => {
          const locked = isItemLocked?.(item) === true;
          return (
            <ServicePortCard
              key={`${item.name || 'service'}-${index}`}
              item={item}
              index={index}
              canEdit={canEdit && !locked}
              /** 编辑态始终展示删除；至少一条由 Form 校验约束，不在此隐藏按钮 */
              canDelete={canRemove && !locked}
              onEdit={() => openEdit(index)}
              onDelete={() => onRemove(index)}
            />
          );
        })}
        {canAdd && (maxCount == null || serviceList.length < maxCount) ? <ServicePortAddCard onClick={openAdd} /> : null}
      </div>
      <ServicePortModal
        visible={modalVisible}
        form={modalForm}
        isDefault={modalIsDefault}
        usageScenarioOptions={usageScenarioOptions}
        onOk={handleModalOk}
        onCancel={closeModal}
      />
    </>
  );
}

/** 供 Form.Item rules 使用的校验器 */
export function createServiceListValidator(
  requireAtLeastOne: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  maxCount?: number,
) {
  return {
    validator(value: unknown, callback: (message?: string) => void) {
      const list = readServiceListFromFormValue(value);
      if (requireAtLeastOne && !list.length) {
        callback(
          t('data-center:please.add.at.least.one', {
            label: t('data-center:maas.inference.service.title'),
          }),
        );
        return;
      }
      if (typeof maxCount === 'number' && list.length > maxCount) {
        callback(
          t('data-center:maas.inference.service.max', {
            max: maxCount,
            label: t('data-center:maas.inference.service.title'),
          }),
        );
        return;
      }
      const missingPort = list.some((item) => {
        const port = Number(item?.internalPort);
        return !Number.isFinite(port) || port <= 0;
      });
      if (missingPort) {
        callback(t('common:validate.required', { name: t('common:internal.port') }));
        return;
      }
      const uniqueScenarioError = getUniqueUsageScenarioError(list);
      if (uniqueScenarioError) {
        callback(uniqueScenarioError);
        return;
      }
      const duplicateNameError = getDuplicateServiceNameError(list);
      if (duplicateNameError) {
        callback(duplicateNameError);
        return;
      }
      const duplicatePortError = getDuplicateServicePortError(list);
      if (duplicatePortError) {
        callback(duplicatePortError);
        return;
      }
      callback();
    },
  };
}

export type { FormInstance };
